import React, { useCallback, useMemo, useRef } from 'react';
import { MultiOptionsFilterComponent } from 'features/insights/components/compare-bar/report-actions-bar/advance-filters-modal/multi-entities-filter/types';
import {
    BasicDropdownMenu,
    BasicMenuItemProps,
    Dropdown,
    SingleSelect,
    UnifiedSelectBox,
} from '@placer-ui/components';
import styles from './hours-range-filter.module.scss';
import { createTimeOptions } from 'features/insights/consts/filters/time-of-day-filter';
import { APP_CONSTS } from 'core/constants/app-consts';
import { FilterOperator } from '@placer-ui/types';
import { getFeatureFlags } from 'core/flow-control';

const TIME_OPTIONS = createTimeOptions();
const TIME_OPERATOR = APP_CONSTS.filter.operators.BETWEEN as FilterOperator;
const MENU_WIDTH = 160;

export const HoursRangeFilter: MultiOptionsFilterComponent<string[]> = ({
    filter,
    onChange,
    ariaLabel,
}) => {
    const { accessibility_advance_filters_modal_ff } = getFeatureFlags();
    const [startHour, endHour] = filter?.value[0].split('-') ?? [];
    const triggerRef = useRef<HTMLDivElement>(null);

    const startIndex = TIME_OPTIONS.findIndex((option) => option.value === startHour);

    const onStartChange = useCallback(
        (item: BasicMenuItemProps<string>) => {
            triggerRef.current?.focus();
            const startIndex = TIME_OPTIONS.findIndex((option) => option.value === item.value);
            let endIndex = TIME_OPTIONS.findIndex((option) => option.value === endHour);
            endIndex = Math.max(endIndex, startIndex + 1);
            const newEnd = TIME_OPTIONS[endIndex].value;
            onChange({
                operator: TIME_OPERATOR,
                value: [`${item.value}-${newEnd}`],
            });
        },
        [endHour, onChange],
    );

    const onEndChange = useCallback(
        (item: BasicMenuItemProps<string>) => {
            onChange({
                operator: TIME_OPERATOR,
                value: [`${startHour}-${item.value}`],
            });
        },
        [onChange, startHour],
    );

    const startingOptions = useMemo(
        () =>
            TIME_OPTIONS.slice(0, -1).map((option) => ({
                ...option,
                isSelected: option.value === startHour,
            })),
        [startHour],
    );

    const endOptions = useMemo(
        () =>
            TIME_OPTIONS.slice(1).map((option, i) => ({
                ...option,
                isSelected: option.value === endHour,
                isDisabled: i < startIndex,
            })),
        [endHour, startIndex],
    );

    const startTriggerValue = startingOptions.find(({ isSelected }) => isSelected)?.label;
    const endTriggerValue = endOptions.find(({ isSelected }) => isSelected)?.label;

    return (
        <div className={styles.container}>
            <div>
                {accessibility_advance_filters_modal_ff ? (
                    <Dropdown
                        appendToParent
                        closeOnSelection
                        trigger={
                            <UnifiedSelectBox
                                ref={triggerRef}
                                aria-label={`${ariaLabel} start range`}
                                testId="start-hour-filter"
                                value={startTriggerValue}
                            />
                        }
                        menuWidth={MENU_WIDTH}
                    >
                        <BasicDropdownMenu
                            items={startingOptions}
                            onItemSelection={onStartChange}
                        />
                    </Dropdown>
                ) : (
                    <SingleSelect
                        align="end"
                        side="bottom"
                        testId="start-hour-filter"
                        items={startingOptions}
                        maxVisibleItems={5}
                        onItemSelection={onStartChange}
                        appendToParent
                    />
                )}
            </div>
            -
            {accessibility_advance_filters_modal_ff ? (
                <Dropdown
                    appendToParent
                    closeOnSelection
                    trigger={
                        <UnifiedSelectBox
                            testId="end-hour-filter"
                            value={endTriggerValue}
                            aria-label={`${ariaLabel} end range`}
                        />
                    }
                    menuWidth={MENU_WIDTH}
                >
                    <BasicDropdownMenu items={endOptions} onItemSelection={onEndChange} />
                </Dropdown>
            ) : (
                <SingleSelect
                    align="start"
                    side="bottom"
                    testId="end-hour-filter"
                    items={endOptions}
                    maxVisibleItems={5}
                    onItemSelection={onEndChange}
                    appendToParent
                />
            )}
        </div>
    );
};
